import {
    getScheduleEntityLock,
    getScheduleFieldLock,
    ScheduleFieldLockSource,
} from "@common/helpers/schedule-transaction-field-lock";
import { isMatchedToInvoiceOrBill } from "@common/helpers/transactions";

interface InvoiceGateTransaction {
    billableExpenseInvoiceId?: string | null;
}

interface RemoveCustomerGateTransaction extends InvoiceGateTransaction {
    customerId?: string | null;
}

interface InvoiceGateParams<T extends InvoiceGateTransaction> {
    selectedArray: T[];
    hasSelectedAll: boolean;
}

/**
 * Once a transaction has been billed via a native invoice, its billable
 * customer is locked server-side (CustomerLockedByBillableExpenseError). We
 * block bulk set/remove customer for the whole selection when any selected
 * transaction is already invoiced, since the backend would reject it for those
 * rows anyway.
 *
 * In "select all" mode the selection is defined by the current filters, not by
 * the loaded rows in `selectedArray`, so we cannot verify the full filtered set
 * is free of invoiced transactions — treat it conservatively as containing one
 * to avoid offering an action the backend would reject.
 */
export function hasInvoicedTransactionsInSelection<
    T extends InvoiceGateTransaction,
>({ selectedArray, hasSelectedAll }: InvoiceGateParams<T>): boolean {
    return (
        hasSelectedAll ||
        selectedArray.some(
            (transaction) => transaction.billableExpenseInvoiceId != null,
        )
    );
}

export function shouldShowRemoveCustomerButton<
    T extends RemoveCustomerGateTransaction,
>({ selectedArray, hasSelectedAll }: InvoiceGateParams<T>): boolean {
    return (
        !hasInvoicedTransactionsInSelection({
            selectedArray,
            hasSelectedAll,
        }) &&
        selectedArray.some((transaction) => transaction.customerId != null)
    );
}

interface MatchGateTransaction {
    amount: number;
    pairedTransferId?: number | null;
    financialAccount: { id: number };
}

interface MatchGateParams<T extends MatchGateTransaction> {
    selectedArray: T[];
    hasSelectedAll: boolean;
}

/**
 * Partitions a selection into the transfer-match pairs it implies, or returns
 * null when the selection cannot be paired without ambiguity.
 *
 * A selection is only pairable when:
 * - it holds an even number of transactions,
 * - every amount appears exactly once and its exact opposite is also present —
 *   the pairs are fully determined by the amounts, so there is no ambiguity in
 *   how they are formed (zero amounts never pair),
 * - the two legs of each pair live in different financial accounts — a
 *   transfer moves money between accounts, so a same-account pair is not a
 *   valid transfer,
 * - no leg is already paired — matching an already-paired transaction silently
 *   unmatches its current partner, so it is never offered here.
 *
 * Each pair is returned as [origin, target] with the positive-amount leg first.
 */
export function getTransferMatchPairs<T extends MatchGateTransaction>(
    selectedArray: T[],
): Array<[T, T]> | null {
    if (
        selectedArray.length < 2 ||
        selectedArray.length % 2 !== 0 ||
        selectedArray.some(
            (transaction) =>
                transaction.amount === 0 ||
                transaction.pairedTransferId != null,
        )
    ) {
        return null;
    }

    const byAmount = new Map<number, T>();
    for (const transaction of selectedArray) {
        if (byAmount.has(transaction.amount)) {
            // The same amount twice would leave the pairing ambiguous.
            return null;
        }
        byAmount.set(transaction.amount, transaction);
    }

    const pairs: Array<[T, T]> = [];
    for (const [amount, origin] of byAmount) {
        const target = byAmount.get(-amount);
        if (!target) {
            return null;
        }
        if (amount > 0) {
            if (origin.financialAccount.id === target.financialAccount.id) {
                return null;
            }
            pairs.push([origin, target]);
        }
    }

    return pairs;
}

/**
 * A selection can be matched in bulk when it decomposes into fully determined
 * transfer pairs (see getTransferMatchPairs) — either the classic exactly-two
 * pair, or a larger even selection where every amount appears exactly once and
 * pairs with its opposite without ambiguity.
 *
 * In "select all" mode the selection is defined by the current filters, not by
 * the loaded rows in `selectedArray`, so the pairing cannot be verified — the
 * action is withheld there.
 */
export function shouldShowMatchButton<T extends MatchGateTransaction>({
    selectedArray,
    hasSelectedAll,
}: MatchGateParams<T>): boolean {
    if (hasSelectedAll) {
        return false;
    }

    return getTransferMatchPairs(selectedArray) != null;
}

/**
 * Bulk apply posts one schedule application per transaction, using that
 * transaction's own amount and date. That does not hold in "select all" mode,
 * where the selection is a set of filters rather than loaded rows, so the
 * action is withheld there. A selection spanning entities is fine: the list
 * offers the policies assigned to every one of them.
 */
export function shouldShowSetScheduleButton({
    selectedCount,
    hasSelectedAll,
}: {
    selectedCount: number;
    hasSelectedAll: boolean;
}): boolean {
    return !hasSelectedAll && selectedCount > 0;
}

interface SuggestedInvoiceMatchTransaction {
    id: number;
    accrualMatchSuggestions?: ReadonlyArray<{
        invoice?: object | null;
    }> | null;
}

export function hasSuggestedInvoiceMatch(
    transaction: SuggestedInvoiceMatchTransaction,
): boolean {
    return (transaction.accrualMatchSuggestions ?? []).some(
        (suggestion) => suggestion.invoice != null,
    );
}

interface SuggestedMatchSelectionParams<T> {
    selectedArray: T[];
    hasSuggestedMatch: (transaction: T) => boolean;
}

/**
 * Shown when any loaded selected transaction carries the suggested match.
 * Withheld in select-all mode: the selection is a filter set, so the
 * suggestion cannot be verified and bulk approve would also accept other
 * match kinds.
 */
export function shouldShowAcceptSuggestedMatchesButton<T>({
    selectedArray,
    hasSelectedAll,
    hasSuggestedMatch,
}: SuggestedMatchSelectionParams<T> & { hasSelectedAll: boolean }): boolean {
    if (hasSelectedAll) {
        return false;
    }

    return selectedArray.some(hasSuggestedMatch);
}

export function getSuggestedMatchTransactionIds<T extends { id: number }>({
    selectedArray,
    hasSuggestedMatch,
}: SuggestedMatchSelectionParams<T>): number[] {
    return selectedArray
        .filter(hasSuggestedMatch)
        .map((transaction) => transaction.id);
}

interface SuggestedBillMatchTransaction {
    id: number;
    accrualMatchSuggestions?: ReadonlyArray<{
        invoice?: object | null;
        bill?: object | null;
    }> | null;
}

/**
 * Bulk approve prefers the invoice suggestion when a transaction carries
 * both, so a transaction only counts as a bill match when it has no invoice
 * suggestion.
 */
export function hasSuggestedBillMatch(
    transaction: SuggestedBillMatchTransaction,
): boolean {
    const suggestions = transaction.accrualMatchSuggestions ?? [];
    return (
        suggestions.some((suggestion) => suggestion.bill != null) &&
        !suggestions.some((suggestion) => suggestion.invoice != null)
    );
}

interface AccrualMatchLike {
    invoice?: object | null;
    bill?: object | null;
    pairedTransaction?: object | null;
}

interface InvoiceAccrualMatchTransaction {
    id: number;
    accrualMatches?: readonly AccrualMatchLike[] | null;
    splits?: ReadonlyArray<{
        accrualMatches?: readonly AccrualMatchLike[] | null;
    } | null> | null;
}

/**
 * True when the transaction, or one of its split children, is matched to an
 * invoice through an accrual match - the matches the bulk "Unmatch invoices"
 * undoes. Bill and transfer matches do not count.
 */
export function hasInvoiceAccrualMatch(
    transaction: InvoiceAccrualMatchTransaction,
): boolean {
    const isInvoiceMatch = (match: AccrualMatchLike) => match.invoice != null;
    return (
        (transaction.accrualMatches ?? []).some(isInvoiceMatch) ||
        (transaction.splits ?? []).some((split) =>
            (split?.accrualMatches ?? []).some(isInvoiceMatch),
        )
    );
}

/**
 * True when the bulk "Unmatch invoices" has something to do for the
 * transaction: an invoice match to undo, or a suggested invoice match to drop.
 */
export function hasUnmatchableInvoiceMatch(
    transaction: InvoiceAccrualMatchTransaction &
        SuggestedInvoiceMatchTransaction,
): boolean {
    return (
        hasInvoiceAccrualMatch(transaction) ||
        hasSuggestedInvoiceMatch(transaction)
    );
}

/**
 * Shown when any loaded selected transaction is matched to an invoice or only
 * carries a suggested invoice match. Withheld in select-all mode: the
 * selection is a filter set, so which transactions qualify cannot be verified.
 */
export function shouldShowUnmatchInvoicesButton({
    selectedArray,
    hasSelectedAll,
}: {
    selectedArray: Array<
        InvoiceAccrualMatchTransaction & SuggestedInvoiceMatchTransaction
    >;
    hasSelectedAll: boolean;
}): boolean {
    return !hasSelectedAll && selectedArray.some(hasUnmatchableInvoiceMatch);
}

interface InvoiceOrBillMatchTransaction {
    transactionMatches?: Array<{
        invoice?: object | null;
        bill?: object | null;
        invoiceId?: string | null;
        billId?: string | null;
    }> | null;
    accrualMatches?: Array<{
        invoice?: object | null;
        bill?: object | null;
        invoiceId?: string | null;
        billId?: string | null;
    }> | null;
}

interface PartitionSelectionParams<T> {
    selectedArray: T[];
    hasSelectedAll: boolean;
}

interface PartitionSelectionByScheduleLockParams<
    T,
> extends PartitionSelectionParams<T> {
    // The accrual ledger the bulk action writes to (the entity's primary
    // accrual ledger); a schedule on another ledger does not lock the row.
    ledgerId: string | null | undefined;
}

export interface LockPartition<T> {
    unlockedTransactions: T[];
    lockedCount: number;
    // True when every selected transaction is locked, so the bulk action is
    // not offered at all rather than shown disabled.
    allLocked: boolean;
}

/**
 * Once a transaction is matched to an invoice or bill, its class
 * assignments and counterparty are locked server-side
 * (MatchedTransactionFieldLockedError — see
 * matched-transaction-field-locked.error.ts). Partitions the current bulk
 * selection so the Set class / Set counterparty bulk actions can (a) hide
 * entirely when every selected transaction is locked, and (b) when only
 * some are locked, apply the change to the unlocked subset only and surface
 * the split count in the action's heading rather than either silently
 * dropping the locked rows or failing the whole batch (the backend's
 * `bulkUpdate` treats any per-item build failure — including this lock —
 * as a partial failure that throws for the WHOLE chunk, even though the
 * unlocked rows in it already persisted; see
 * FinancialTransactionBulkUpdateInternalService#execute).
 *
 * In "select all" mode the selection is defined by filters, not loaded
 * rows, so lock status cannot be verified per-transaction — the partition
 * is skipped there (same conservative posture as
 * `hasInvoicedTransactionsInSelection`) and every transaction is treated as
 * unlocked, matching this bulk action's pre-existing behavior in that mode.
 */
export function partitionSelectionByInvoiceOrBillLock<
    T extends InvoiceOrBillMatchTransaction,
>({
    selectedArray,
    hasSelectedAll,
}: PartitionSelectionParams<T>): LockPartition<T> {
    return partitionSelectionByLock({
        selectedArray,
        hasSelectedAll,
        isLocked: (t) =>
            isMatchedToInvoiceOrBill(t.transactionMatches, t.accrualMatches),
    });
}

interface ScheduleLockTransaction {
    schedule?: ScheduleFieldLockSource | null;
}

/**
 * Same shape as the invoice/bill partition, for the recognition-schedule
 * lock on the Accrual ledger: a live schedule owns the transaction's accrual
 * account (ScheduledTransactionAccrualLedgerLockedError), so the Set accrual
 * ledger bulk action skips those rows and says so in its heading.
 */
export function partitionSelectionByScheduleLock<
    T extends ScheduleLockTransaction,
>({
    selectedArray,
    hasSelectedAll,
    ledgerId,
}: PartitionSelectionByScheduleLockParams<T>): LockPartition<T> {
    return partitionSelectionByLock({
        selectedArray,
        hasSelectedAll,
        isLocked: (t) =>
            getScheduleFieldLock({ schedule: t.schedule, ledgerId }).isLocked,
    });
}

/**
 * A live schedule on any ledger keeps the transaction in its entity
 * (ScheduledTransactionEntityLockedError), so Set entity skips those rows and
 * says so in its heading.
 */
export function partitionSelectionByScheduleEntityLock<
    T extends ScheduleLockTransaction,
>({
    selectedArray,
    hasSelectedAll,
}: PartitionSelectionParams<T>): LockPartition<T> {
    return partitionSelectionByLock({
        selectedArray,
        hasSelectedAll,
        isLocked: (t) =>
            getScheduleEntityLock({ schedule: t.schedule }).isLocked,
    });
}

function partitionSelectionByLock<T>({
    selectedArray,
    hasSelectedAll,
    isLocked,
}: {
    selectedArray: T[];
    hasSelectedAll: boolean;
    isLocked: (transaction: T) => boolean;
}): LockPartition<T> {
    if (hasSelectedAll) {
        return {
            unlockedTransactions: selectedArray,
            lockedCount: 0,
            allLocked: false,
        };
    }

    const unlockedTransactions = selectedArray.filter((t) => !isLocked(t));
    const lockedCount = selectedArray.length - unlockedTransactions.length;

    return {
        unlockedTransactions,
        lockedCount,
        allLocked:
            selectedArray.length > 0 && lockedCount === selectedArray.length,
    };
}
